import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { lt } from "../localize";
import { PositionDiagram } from "./PositionDiagram";
import type { LessonStage } from "../types";

interface StageConceptProps {
  stage: LessonStage;
}

export const StageConcept = ({ stage }: StageConceptProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const cards = stage.conceptCards ?? [];
  const [current, setCurrent] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"right" | "left">("right");
  const [animKey, setAnimKey] = useState(0);
  const prevIndex = useRef(0);

  if (cards.length === 0) return null;

  const card = cards[current];

  const goTo = (index: number) => {
    const dir = index > prevIndex.current ? "right" : "left";
    setSlideDirection(dir);
    setAnimKey((k) => k + 1);
    prevIndex.current = index;
    setCurrent(index);
  };

  const goNext = () => {
    if (current < cards.length - 1) goTo(current + 1);
  };

  const goPrev = () => {
    if (current > 0) goTo(current - 1);
  };

  // Check if any card uses position highlighting
  const hasPositionDiagram = cards.some((c) => c.positionHighlight && c.positionHighlight.length > 0);
  const currentHighlight = card.positionHighlight ?? [];

  return (
    <div className="space-y-4">
      {/* Position diagram — persistent when lesson involves positions */}
      {hasPositionDiagram && (
        <div className="py-2">
          <PositionDiagram highlighted={currentHighlight} />
        </div>
      )}

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5">
        {cards.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className="w-2 h-2 rounded-full transition-all"
            style={
              i === current
                ? { backgroundColor: "hsl(var(--level-accent))", width: "1rem" }
                : i < current
                  ? { backgroundColor: "hsl(var(--level-accent) / 0.5)" }
                  : { backgroundColor: "rgba(255,255,255,0.2)" }
            }
          />
        ))}
      </div>

      {/* Card with slide animation */}
      <div
        key={animKey}
        className="glass relative overflow-hidden"
        style={{
          minHeight: 250,
          animation: `${slideDirection === "right" ? "slideCardFromRight" : "slideCardFromLeft"} 0.35s ease-out`,
        }}
      >
        {/* Colored top strip */}
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ backgroundColor: "hsl(var(--level-accent))" }}
        />

        {/* Card number badge */}
        <div
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold"
          style={{
            backgroundColor: "hsl(var(--level-accent) / 0.15)",
            color: "hsl(var(--level-accent))",
          }}
        >
          {current + 1}/{cards.length}
        </div>

        <div className="p-5 pt-6">
          {/* Title with optional visual hint */}
          <h3
            className="text-xl font-bold mb-3 pr-12"
            style={{ color: "hsl(var(--level-accent))" }}
          >
            {card.visual && (
              <span className="mr-1.5" role="img" aria-label="visual hint">
                {{"hole-cards": "🃏", "community": "🎴", "position": "📍", "chips": "💰", "strategy": "🎯", "math": "📊", "odds": "🧮", "bluff": "🎭", "read": "👁️", "tournament": "🏆"}[card.visual] || "📌"}
              </span>
            )}
            {lt(card.title, lang)}
          </h3>

          <p className="text-sm text-muted-foreground leading-relaxed">
            {lt(card.content, lang)}
          </p>
        </div>
      </div>

      {/* Navigation arrows — prominent filled circles */}
      <div className="flex items-center justify-between px-2">
        <button
          onClick={goPrev}
          disabled={current === 0}
          className="w-10 h-10 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-all hover:bg-white/[0.08]"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-xs text-muted-foreground">
          {current + 1} / {cards.length}
        </span>
        <button
          onClick={goNext}
          disabled={current === cards.length - 1}
          className="w-10 h-10 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-all hover:bg-white/[0.08]"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
